import { useCallback, useEffect, useState } from "react";
import { request } from "graphql-request";

// Careers shares the case-studies Hygraph project, so it reuses that key. The
// key is baked into the bundle at build time and is public; it is scoped to
// published read-only in Hygraph's Public Content API settings.
export const CAREERS_ENDPOINT = `https://us-east-1-shared-usea1-02.cdn.hygraph.com/content/${process.env.REACT_APP_CS_KEY}/master`;

// TeamGroup enum values come back from Hygraph verbatim (camelCase). This is the
// single place their display text lives: it drives the listing badge, the detail
// page eyebrow and the filter dropdown. An unrecognised value falls through to
// the raw string so a new enum renders something rather than an empty badge.
export const TEAM_LABELS = {
    pmgOperations: "PMG Operations",
    pmgProjects: "PMG Projects",
    pmgCreative: "PMG Creative",
};

export const teamLabel = (group) => TEAM_LABELS[group] || group || "";

const ROLE_FIELDS = `
    id
    title
    slug
    group
    summary
    location
    workModel
    employment
    highlights
    overviewHeading
    overviewBody
    responsibilities
    qualifications
    niceToHave
    whyJoinUs
    keyDetails
    applyUrl
`;

const ALL_ROLES = `{ jobRoles(orderBy: sortOrder_ASC, stage: PUBLISHED) { ${ROLE_FIELDS} } }`;

const ROLE_BY_SLUG = `
    query RoleBySlug($slug: String!) {
        jobRoles(where: { slug: $slug }, stage: PUBLISHED, first: 1) { ${ROLE_FIELDS} }
    }
`;

// Editors paste bare domains ("partneredmg.com"). Used as an href that resolves
// as a relative path — /careers/partneredmg.com — and 404s. Anything not already
// absolute or root-relative gets https:// put in front of it.
export const normaliseUrl = (url) => {
    const trimmed = (url || "").trim();
    if (!trimmed) return null;
    if (/^https?:\/\//i.test(trimmed)) return trimmed;
    if (trimmed.startsWith("/")) return trimmed;
    return `https://${trimmed}`;
};

// keyDetails can be configured in Hygraph either as one multi-line string or as
// a multi-value list. Both land here as an array of lines, so the component does
// not care which way the field was set up.
export const toLines = (value) => {
    if (Array.isArray(value)) return value.map((v) => (v || "").trim()).filter(Boolean);
    if (typeof value === "string") return value.split(/\r?\n/).map((v) => v.trim()).filter(Boolean);
    return [];
};

// Hygraph returns null for an empty multi-value field; every list the UI maps
// over is normalised here so no component has to guard.
const normalise = (role) => ({
    ...role,
    teamLabel: teamLabel(role.group),
    applyUrl: normaliseUrl(role.applyUrl),
    keyDetails: toLines(role.keyDetails),
    // the listing card's metadata line, derived rather than stored, so it can
    // never disagree with the two fields it is built from
    listingMeta: [role.location, role.workModel].filter(Boolean).join(" · "),
    highlights: role.highlights || [],
    overviewBody: role.overviewBody || [],
    responsibilities: role.responsibilities || [],
    qualifications: role.qualifications || [],
    niceToHave: role.niceToHave || [],
    whyJoinUs: role.whyJoinUs || [],
});

// Shared fetch state machine. `error` is distinct from an empty result: no roles
// published is a valid answer, a failed request is not, and a careers page that
// silently renders empty reads as "we are not hiring".
const useHygraph = (run, deps) => {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [attempt, setAttempt] = useState(0);

    const retry = useCallback(() => setAttempt((a) => a + 1), []);

    useEffect(() => {
        let cancelled = false;
        setLoading(true);
        setError(null);

        if (!process.env.REACT_APP_CS_KEY) {
            setError(new Error("Careers content is not configured."));
            setLoading(false);
            return undefined;
        }

        run()
            .then((result) => {
                if (cancelled) return;
                setData(result);
                setLoading(false);
            })
            .catch((err) => {
                if (cancelled) return;
                setError(err);
                setLoading(false);
            });

        return () => {
            cancelled = true;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [...deps, attempt]);

    return { data, loading, error, retry };
};

export const useJobRoles = () => {
    const { data, loading, error, retry } = useHygraph(
        () => request(CAREERS_ENDPOINT, ALL_ROLES).then((r) => (r.jobRoles || []).map(normalise)),
        []
    );
    return { roles: data, loading, error, retry };
};

export const useJobRole = (slug) => {
    const { data, loading, error, retry } = useHygraph(
        () =>
            request(CAREERS_ENDPOINT, ROLE_BY_SLUG, { slug }).then((r) => {
                const [found] = r.jobRoles || [];
                return found ? normalise(found) : null;
            }),
        [slug]
    );
    // data === null after a successful load means the slug does not exist
    return { role: data, loading, error, retry, notFound: !loading && !error && data === null };
};
